import { Button, Input } from "@aix/ui"
import { ReactTable } from "@aix/ui"
import { CheckCircle, MessageCircle, XCircle } from "lucide-react"
import { useMemo, useState } from "react"
import toast from "react-hot-toast"
import { trpc } from "../../lib/trpc"

export default function AdminQuestionsScreen() {
  const [statusFilter, setStatusFilter] = useState<"pending" | "resolved" | "ignored">("pending")
  const [resolvingQuestion, setResolvingQuestion] = useState<any>(null)

  const {
    data: questions,
    isLoading,
    refetch,
  } = trpc.videoOnboarding.listUnansweredQuestions.useQuery({
    status: statusFilter,
  })

  const resolveQuestion = trpc.videoOnboarding.resolveQuestion.useMutation({
    onSuccess: () => {
      toast.success("Question resolved successfully!")
      setResolvingQuestion(null)
      void refetch()
    },
    onError: (error) => {
      toast.error(error.message || "Failed to resolve question")
    },
  })

  const columns = useMemo(
    () => [
      {
        Header: "Question",
        accessor: "questionText",
        Cell: ({ row }: any) => {
          const question = row.original || row
          return (
            <div>
              <div className="font-semibold text-primary mb-1">{question.questionText}</div>
              <div className="text-sm text-muted-foreground">
                Confidence: {question.confidenceScore}% | Rating:{" "}
                {question.userRating === "thumbs_up" ? "👍" : question.userRating === "thumbs_down" ? "👎" : "-"}
              </div>
            </div>
          )
        },
      },
      {
        Header: "Chatbot Answer",
        accessor: "chatbotAnswer",
        Cell: ({ row }: any) => {
          const question = row.original || row
          return <div className="text-sm text-muted-foreground line-clamp-2">{question.chatbotAnswer}</div>
        },
      },
      {
        Header: "Date",
        accessor: "createdAt",
        Cell: ({ row }: any) => {
          const question = row.original || row
          return (
            <div className="text-sm text-muted-foreground">{new Date(question.createdAt).toLocaleDateString()}</div>
          )
        },
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ row }: any) => {
          const question = row.original || row
          return (
            <span
              className={`px-2 py-1 rounded text-xs font-medium ${
                question.status === "resolved"
                  ? "bg-green-500/10 text-green-500"
                  : question.status === "ignored"
                    ? "bg-gray-500/10 text-gray-500"
                    : "bg-yellow-500/10 text-yellow-500"
              }`}
            >
              {question.status}
            </span>
          )
        },
      },
      {
        Header: "Actions",
        accessor: "id",
        Cell: ({ row }: any) => {
          const question = row.original || row
          return (
            <div className="flex gap-2">
              {question.status === "pending" && (
                <button
                  onClick={() => setResolvingQuestion(question)}
                  className="p-2 hover:bg-accent rounded transition-colors group relative"
                  title="Resolve question"
                >
                  <MessageCircle className="w-4 h-4 text-primary" />
                  <div className="absolute left-0 top-full mt-2 w-32 bg-gray-900 text-white text-xs rounded-lg p-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 shadow-xl">
                    Resolve question
                  </div>
                </button>
              )}
              {question.status === "resolved" && question.adminAnswer && (
                <button
                  onClick={() => setResolvingQuestion(question)}
                  className="p-2 hover:bg-accent rounded transition-colors group relative"
                  title="View answer"
                >
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <div className="absolute left-0 top-full mt-2 w-24 bg-gray-900 text-white text-xs rounded-lg p-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 shadow-xl">
                    View answer
                  </div>
                </button>
              )}
            </div>
          )
        },
      },
    ],
    [],
  )

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-primary mb-2">Questions Queue</h1>
          <p className="text-muted-foreground">Review and answer user questions</p>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setStatusFilter("pending")}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              statusFilter === "pending"
                ? "bg-primary text-primary-foreground"
                : "bg-secondary text-secondary-foreground hover:bg-accent"
            }`}
          >
            Pending
          </button>
          <button
            onClick={() => setStatusFilter("resolved")}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              statusFilter === "resolved"
                ? "bg-primary text-primary-foreground"
                : "bg-secondary text-secondary-foreground hover:bg-accent"
            }`}
          >
            Resolved
          </button>
          <button
            onClick={() => setStatusFilter("ignored")}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              statusFilter === "ignored"
                ? "bg-primary text-primary-foreground"
                : "bg-secondary text-secondary-foreground hover:bg-accent"
            }`}
          >
            Ignored
          </button>
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : questions && questions.length > 0 ? (
          <ReactTable data={questions} columns={columns} />
        ) : (
          <div className="text-center py-12 bg-secondary rounded-lg">
            <MessageCircle className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No {statusFilter} questions</p>
          </div>
        )}
      </div>

      {/* Resolve Question Modal */}
      {resolvingQuestion && (
        <ResolveQuestionModal
          question={resolvingQuestion}
          onClose={() => setResolvingQuestion(null)}
          onSubmit={(data) => resolveQuestion.mutate({ questionId: resolvingQuestion.id, ...data })}
          isLoading={resolveQuestion.isLoading}
        />
      )}
    </div>
  )
}

// Resolve Question Modal
function ResolveQuestionModal({
  question,
  onClose,
  onSubmit,
  isLoading,
}: {
  question: any
  onClose: () => void
  onSubmit: (data: any) => void
  isLoading: boolean
}) {
  const [adminAnswer, setAdminAnswer] = useState(question.adminAnswer || "")
  const [createKnowledgeBase, setCreateKnowledgeBase] = useState(false)
  const [kbQuestion, setKbQuestion] = useState(question.questionText || "")
  const [kbAnswer, setKbAnswer] = useState("")

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit({
      adminAnswer,
      createKnowledgeBase,
      knowledgeBaseEntry: createKnowledgeBase
        ? {
            question: kbQuestion,
            answer: kbAnswer || adminAnswer,
          }
        : undefined,
    })
  }

  const isResolved = question.status === "resolved"

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-background rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-2xl font-bold text-primary mb-4">{isResolved ? "View" : "Resolve"} Question</h2>

        {/* Question Details */}
        <div className="bg-secondary rounded-lg p-4 mb-4">
          <div className="mb-3">
            <span className="text-sm font-semibold text-muted-foreground">User Question:</span>
            <p className="text-primary mt-1">{question.questionText}</p>
          </div>
          <div className="mb-3">
            <span className="text-sm font-semibold text-muted-foreground">Chatbot Answer:</span>
            <p className="text-muted-foreground text-sm mt-1">{question.chatbotAnswer}</p>
          </div>
          <div className="flex gap-4 text-sm">
            <div>
              <span className="font-semibold text-muted-foreground">Confidence:</span> {question.confidenceScore}%
            </div>
            <div>
              <span className="font-semibold text-muted-foreground">Rating:</span>{" "}
              {question.userRating === "thumbs_up" ? "👍" : question.userRating === "thumbs_down" ? "👎" : "-"}
            </div>
            <div>
              <span className="font-semibold text-muted-foreground">Date:</span>{" "}
              {new Date(question.createdAt).toLocaleDateString()}
            </div>
          </div>
        </div>

        {isResolved ? (
          /* View Mode */
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1">Admin Answer:</label>
              <div className="bg-secondary rounded-lg p-4">{question.adminAnswer}</div>
            </div>
            <div className="flex justify-end">
              <Button onClick={onClose}>Close</Button>
            </div>
          </div>
        ) : (
          /* Edit Mode */
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1">Your Answer *</label>
              <textarea
                value={adminAnswer}
                onChange={(e) => setAdminAnswer(e.target.value)}
                placeholder="Provide a better answer..."
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                rows={4}
                required
              />
            </div>

            {/* Knowledge Base Option */}
            <div className="border-t border-border pt-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={createKnowledgeBase}
                  onChange={(e) => setCreateKnowledgeBase(e.target.checked)}
                  className="w-4 h-4"
                />
                <span className="text-sm font-medium">Add to Knowledge Base (for future questions)</span>
              </label>

              {createKnowledgeBase && (
                <div className="mt-3 space-y-3 bg-secondary p-4 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-1">
                      Knowledge Base Question
                    </label>
                    <Input
                      type="text"
                      value={kbQuestion}
                      onChange={(e) => setKbQuestion(e.target.value)}
                      placeholder="Generalize the question..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-1">
                      Knowledge Base Answer (optional - will use admin answer)
                    </label>
                    <textarea
                      value={kbAnswer}
                      onChange={(e) => setKbAnswer(e.target.value)}
                      placeholder="Leave blank to use your answer above..."
                      className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                      rows={3}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={onClose} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" loading={isLoading} className="flex-1">
                Resolve Question
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
